import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Drawer } from 'antd';
import { SiderItemsProps } from '../interfaces/sidebar-interfaces';
import { UpOutlined, MenuOutlined } from '@ant-design/icons';

interface BottomBarProps {
  items: SiderItemsProps[];
}

const BottomBar: React.FC<BottomBarProps> = ({ items }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [moreDrawerVisible, setMoreDrawerVisible] = useState(false);
  const [selectedParent, setSelectedParent] = useState<SiderItemsProps | null>(
    null,
  );

  const shouldShowMore = items.length > 4;
  const mobileItems = shouldShowMore ? items.slice(0, 3) : items;
  const moreItems = shouldShowMore ? items.slice(3) : [];

  const handleItemClick = (item: SiderItemsProps) => {
    if (item.children && item.children.length > 0) {
      setSelectedParent(item);
      setDrawerVisible(true);
    } else {
      navigate(item.key as string);
    }
  };

  const handleSubItemClick = (subItem: SiderItemsProps) => {
    navigate(subItem.key as string);
    setDrawerVisible(false);
  };

  const handleMoreItemClick = (item: SiderItemsProps) => {
    if (item.children && item.children.length > 0) {
      setMoreDrawerVisible(false);
      setSelectedParent(item);
      setDrawerVisible(true);
    } else {
      navigate(item.key as string);
      setMoreDrawerVisible(false);
    }
  };

  const isItemActive = (item: SiderItemsProps) => {
    if (pathname === item.key || pathname.startsWith(item.key + '/')) {
      return true;
    }
    if (item.children) {
      return item.children.some(
        child => pathname === child.key || pathname.startsWith(child.key + '/'),
      );
    }
    return false;
  };

  const isMoreActive = moreItems.some(item => isItemActive(item));

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#003666] border-t-2 border-[#0acaf9] safe-bottom">
        <div className="flex justify-around items-center h-16 px-2">
          {mobileItems.map(item => {
            const isActive = isItemActive(item);
            const hasChildren = item.children && item.children.length > 0;

            return (
              <button
                key={item.key}
                onClick={() => handleItemClick(item)}
                className={`flex flex-col items-center justify-center gap-1 py-2 px-3 rounded-lg transition-all min-w-[60px] relative ${
                  isActive
                    ? 'bg-white bg-opacity-10 text-[#0acaf9]'
                    : 'text-white hover:bg-white hover:bg-opacity-5'
                }`}
              >
                <div className="text-xl flex items-center justify-center">
                  {item.icon}
                  {hasChildren && (
                    <UpOutlined
                      className="text-[8px] ml-1"
                      style={{ transform: 'translateY(1px)' }}
                    />
                  )}
                </div>
                <span className="text-[10px] font-medium leading-tight text-center line-clamp-1">
                  {String(item.label ?? '')
                    .split(' ')
                    .slice(0, 2)
                    .join(' ')}
                </span>
              </button>
            );
          })}

          {shouldShowMore && (
            <button
              onClick={() => setMoreDrawerVisible(true)}
              className={`flex flex-col items-center justify-center gap-1 py-2 px-3 rounded-lg transition-all min-w-[60px] relative ${
                isMoreActive
                  ? 'bg-white bg-opacity-10 text-[#0acaf9]'
                  : 'text-white hover:bg-white hover:bg-opacity-5'
              }`}
            >
              <div className="text-xl flex items-center justify-center">
                <MenuOutlined />
              </div>
              <span className="text-[10px] font-medium leading-tight text-center">
                Më shumë
              </span>
            </button>
          )}
        </div>
      </div>

      <Drawer
        title={
          <div className="flex items-center gap-3">
            {selectedParent?.icon && (
              <span className="text-2xl text-[#003666]">
                {selectedParent.icon}
              </span>
            )}
            <span className="text-[#003666] text-lg font-semibold">
              {selectedParent?.label as string}
            </span>
          </div>
        }
        placement="bottom"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        height="auto"
        className="bottom-drawer-menu"
        styles={{
          body: { padding: '0' },
          header: {
            borderBottom: '2px solid #0acaf9',
            padding: '16px 20px',
          },
        }}
      >
        <div className="pb-4">
          {selectedParent?.children?.map(child => {
            const isChildActive =
              pathname === child.key || pathname.startsWith(child.key + '/');

            return (
              <div
                key={child.key}
                onClick={() => handleSubItemClick(child)}
                className={`flex items-center gap-4 px-6 py-4 cursor-pointer transition-all border-l-4 ${
                  isChildActive
                    ? 'bg-blue-50 border-[#0acaf9] text-[#003666]'
                    : 'border-transparent hover:bg-gray-50'
                }`}
              >
                <span
                  className={`text-xl ${isChildActive ? 'text-[#0acaf9]' : 'text-gray-600'}`}
                >
                  {child.icon}
                </span>
                <span
                  className={`text-base font-medium ${isChildActive ? 'text-[#003666]' : 'text-gray-700'}`}
                >
                  {child.label as string}
                </span>
              </div>
            );
          })}
        </div>
      </Drawer>

      <Drawer
        title={
          <div className="flex items-center gap-3">
            <span className="text-2xl text-[#003666]">
              <MenuOutlined />
            </span>
            <span className="text-[#003666] text-lg font-semibold">
              Më shumë
            </span>
          </div>
        }
        placement="bottom"
        onClose={() => setMoreDrawerVisible(false)}
        open={moreDrawerVisible}
        height="auto"
        className="bottom-drawer-menu"
        styles={{
          body: { padding: '0' },
          header: {
            borderBottom: '2px solid #0acaf9',
            padding: '16px 20px',
          },
        }}
      >
        <div className="pb-4">
          {moreItems.map(item => {
            const isActive = isItemActive(item);
            const hasChildren = item.children && item.children.length > 0;

            return (
              <div
                key={item.key}
                onClick={() => handleMoreItemClick(item)}
                className={`flex items-center gap-4 px-6 py-4 cursor-pointer transition-all border-l-4 ${
                  isActive
                    ? 'bg-blue-50 border-[#0acaf9] text-[#003666]'
                    : 'border-transparent hover:bg-gray-50'
                }`}
              >
                <span
                  className={`text-xl ${isActive ? 'text-[#0acaf9]' : 'text-gray-600'}`}
                >
                  {item.icon}
                </span>
                <div className="flex items-center gap-2 flex-1">
                  <span
                    className={`text-base font-medium ${isActive ? 'text-[#003666]' : 'text-gray-700'}`}
                  >
                    {item.label as string}
                  </span>
                  {hasChildren && (
                    <UpOutlined
                      className="text-[10px] text-gray-400"
                      style={{ transform: 'translateY(1px)' }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Drawer>
    </>
  );
};

export default BottomBar;
